import type { Metadata } from 'next';
import { SUPPORTED_LANGS, type SupportedLang } from '@/lib/lang/supported';

// Next.js REPLACES openGraph/twitter/alternates when a page sets them and
// INHERITS the root's when it doesn't — which is how every page but
// /for-dev-teams ended up with the homepage as its canonical (fixed
// 2026-10-19). Build the whole object here. Titles go WITHOUT the brand: the
// root template appends ' | Apprendo'; use absoluteTitle only when the title
// already names Apprendo.

// Every language Tessa speaks has a marketing site except Portuguese (pt-BR
// is still flagged). Widening SUPPORTED_LANGS makes the compiler list every
// content table below and in lib/marketing/industries that needs the column.
export type SiteLocale = Exclude<SupportedLang, 'pt'>;

export const SITE_LOCALES: readonly SiteLocale[] = SUPPORTED_LANGS.filter(
  (l): l is SiteLocale => l !== 'pt',
);

export const OG_LOCALE: Record<SiteLocale, string> = {
  en: 'en_US',
  es: 'es_MX',
  fr: 'fr_CA',
};

export const BRAND = 'Apprendo';

const DEFAULT_OG_IMAGE = '/api/og?type=default';

interface PageMetadataInput {
  path: string;
  title: string;
  absoluteTitle?: boolean;
  description: string;
  locale?: SiteLocale;
  languages?: Partial<Record<SiteLocale, string>>;
  ogImage?: string;
}

export function pageOgImage({
  title,
  subtitle,
  badge,
  path,
}: {
  title: string;
  subtitle: string;
  badge?: string;
  path: string;
}): string {
  const params = new URLSearchParams({ type: 'page', title, subtitle, url: `apprendo.co${path}` });
  if (badge) params.set('badge', badge);
  return `/api/og?${params.toString()}`;
}

export function pageMetadata({
  path,
  title,
  absoluteTitle = false,
  description,
  locale = 'en',
  languages,
  ogImage = DEFAULT_OG_IMAGE,
}: PageMetadataInput): Metadata {
  const translated = languages
    ? SITE_LOCALES.filter((l) => l !== locale && languages[l])
    : [];

  return {
    title: absoluteTitle ? { absolute: title } : title,
    description,
    alternates: {
      canonical: path,
      ...(languages && {
        languages: {
          ...Object.fromEntries(SITE_LOCALES.filter((l) => languages[l]).map((l) => [l, languages[l]!])),
          'x-default': languages.en ?? path,
        },
      }),
    },
    openGraph: {
      type: 'website',
      siteName: BRAND,
      url: path,
      title,
      description,
      locale: OG_LOCALE[locale],
      ...(translated.length > 0 && { alternateLocale: translated.map((l) => OG_LOCALE[l]) }),
      images: [{ url: ogImage, width: 1200, height: 630, type: 'image/png', alt: title }],
    },
    twitter: {
      card: 'summary_large_image',
      site: '@apprendoio',
      creator: '@apprendoio',
      title,
      description,
      images: [ogImage],
    },
  };
}
